import { EventEmitter, Injectable } from '@angular/core';
import { DataTaskError, DataTaskService } from '@app/core/services/data-task.service';
import { ChangeState, CreateColumnLayoutParams, DataTaskRequest, DBGridOptions, GridColumn, GridColumnLayout, GridColumnsParams, GridDataTask, GridFilterType, GridOptions, GridRow, GridRowChangeEvent, GridRowChanges, GridUserSettings, SaveChangesParams, SaveUserSettingsParams, ServerSideRequestParams } from './grid.types';
import { MatDialog } from '@angular/material/dialog';
import { DatePipe } from '@angular/common';
import { AG_GRID_LOCALE_CN, AG_GRID_LOCALE_DE, AG_GRID_LOCALE_DK, AG_GRID_LOCALE_EN, AG_GRID_LOCALE_ES, AG_GRID_LOCALE_FI, AG_GRID_LOCALE_FR, AG_GRID_LOCALE_IT, AG_GRID_LOCALE_NO, AG_GRID_LOCALE_PL, AG_GRID_LOCALE_SE } from '@ag-grid-community/locale';
import { ProgressModalComponent } from './modals/progress-modal/progress-modal.component';
import { ColDef, ExcelStyle, GridApi, IRowNode } from '@ag-grid-community/core';
import { DatataskEvent, NavigateEvent } from './cell-renderers/grid-functions-cell-renderer/grid-functions-cell-renderer.component';
import { UtilityService } from '@app/core/services/utility.service';
import { StateService } from '@app/core/services/state.service';
import { ModalService } from '../../services/modal.service';
import { Base64UrlService } from '@app/services/base64url.service';
import { GridComponent } from './grid.component';
import { DATE_FORMATS } from '@app/core/models/date-adapter';
import { updateActiveCellEditorFromGridRow } from './helpers/grid-active-editor.helpers';
import { ToastService } from '@app/core/services/toast.service';
import { Subject, concatMap, catchError, EMPTY, Observable, from } from 'rxjs';
import { GridMessagesModalComponent, GridMessagesModalData } from './modals/grid-messages-modal/grid-messages-modal.component';

@Injectable({
    providedIn: 'root',
})
export class GridService {
    private loadedDataTaskColumn: { [key: number]: GridColumn[] } = {};

    public excelStyles: ExcelStyle[] = [
        {
            id: 'numberType',
            numberFormat: {
                format: '0',
            },
        },
        {
            id: 'booleanType',
            dataType: 'Boolean',
        },
        {
            id: 'stringType',
            dataType: 'String',
        },
        {
            id: 'dateTimeType',
            dataType: 'DateTime',
            numberFormat: {
                format: DATE_FORMATS.display.dateInput.format.toLowerCase() + ' hh:mm:ss',
            },
        },
        {
            id: 'dateType',
            dataType: 'DateTime',
            numberFormat: {
                format: DATE_FORMATS.display.dateInput.format.toLowerCase(),
            },
        },
    ];

    constructor(
        private base64: Base64UrlService,
        private datatask: DataTaskService,
        private dialog: MatDialog,
        private utility: UtilityService,
        private state: StateService,
        private modalService: ModalService,
        private toast: ToastService,
    ) {
        this.gridMessages$.pipe(concatMap((message) => this.openGridMessage(message).pipe(catchError(() => EMPTY)))).subscribe();

        window.addEventListener('message', (event) => {
            if ('type' in event.data && typeof event.data.type === 'string' && event.data.type === 'ttData') {
                const popupEvent = <PopupEvent>event.data;

                if (!('messages' in popupEvent)) return;

                let messages: GridMessage[] = [];

                if (typeof popupEvent.messages === 'string') {
                    try {
                        messages = JSON.parse(popupEvent.messages);
                    } catch (error) {
                        console.error(error);
                    }
                } else if (Array.isArray(popupEvent.messages)) {
                    messages = popupEvent.messages;
                }

                if (messages.length > 0) {
                    this.addGridMessage(messages);
                }
            }
        });
    }

    /**
     * The id of the last active grid.
     */
    public activeGridId: null | string = null;

    /**
     * Utility function for base64encode with unicode support
     *
     * @param data string to encode with base64 with unicode support.
     * @returns the encoded base64 string.
     */
    public utoa(data: string): string {
        return btoa(unescape(encodeURIComponent(data)));
    }

    /**
     * Utility function for base64decode with unicode support
     *
     * @param data base64 encoded string to be decodd.
     * @returns the string value of the decoded base64 string.
     */
    public atou(data: string): string {
        return decodeURIComponent(escape(atob(data)));
    }
    /**
     * Retrieves the usersettings with the given variablename. If no method is provided, the default usersettings method `973` is used.
     *
     * @param variablename the name for the variable to retrieve user settings with.
     * @param method the method to use for retrieving usersettings, default is `973`.
     * @returns
     */
    public async getRemember(variablename: string, method: number = 3807): Promise<GridUserSettings | undefined> {
        const response = await this.datatask.Post(method, { variablename: variablename });

        if (response[0].variablevalue) {
            return JSON.parse(response[0].variablevalue);
        }

        return;
    }

    /**
     * Stores the given variablevalue to the db indexed by the given variablename.
     *
     * @param param0 parameters to configure the saving method with.
     * @returns a void promise.
     */
    public saveUserSettings({ method = 616, variablename, variablevalue, isBase64 = true }: SaveUserSettingsParams): Promise<void> {
        if (!method || !variablename) throw Error('Grid-Service: Missing parameters in saveUserSettings');

        return this.datatask.Post(method, {
            variablename: variablename,
            variablevalue: this.utoa(JSON.stringify(variablevalue)),
            is_base64: !!isBase64 ? 1 : 0,
        });
    }

    /**
     * Retrieves columns for the grid with the given params.
     *
     * @param param0 parameters for retrieving grid columns.
     * @returns a promise containing list of grid columns.
     */
    public async getGridColumns({ method = 1999, loadDataMethod, params, force }: GridColumnsParams): Promise<GridColumn[]> {
        if (!this.loadedDataTaskColumn[loadDataMethod] || force === true) {
            let parameters: { [key: string]: string | number | boolean } = {};

            if (params && typeof params === 'object') parameters = { ...params };

            parameters['p2_datatask_keyno'] = loadDataMethod;

            const response = await this.datatask.Post(method, parameters);

            this.loadedDataTaskColumn[loadDataMethod] = JSON.parse(JSON.stringify(response));

            return response;
        } else {
            return JSON.parse(JSON.stringify(this.loadedDataTaskColumn[loadDataMethod]));
        }
    }

    /**
     * Retrieves the row data with the given load data method and parameters.
     *
     * @param method the load data method for retrieving grid row data.
     * @param params the parameters required for retrieving the row data, if any.
     * @returns a promise containg list of grid rows.
     */
    public async getGridData<TType extends GridRow = GridRow>(method: number, params: any, options: GridOptions<TType>): Promise<TType[]> {
        if (!!method && !isNaN(Number(method))) {
            try {
                let data: TType[] = await this.datatask.Post(method, params || {});

                const encodedColumns = Object.entries(options?.data?.columnInfo ?? {})
                    .filter(([_, info]) => info?.base64 === true)
                    .map(([colname, _]) => colname);

                if (encodedColumns.length > 0) {
                    data = data.map((row) => this.getDecodedRow(row, encodedColumns));
                }

                const chipsColumns = Object.keys(data[0]).filter((key) => key.endsWith('_chips'));

                if (chipsColumns.length > 0) {
                    const chipsErrors: unknown[] = [];

                    for (let item of data) {
                        for (let columnKey of chipsColumns) {
                            try {
                                (item as GridRow)[columnKey] = JSON.parse(item[columnKey]);
                            } catch (error) {
                                (item as GridRow)[columnKey] = [];
                                chipsErrors.push({ error: error, item: item });
                            }
                        }
                    }
                    console.log(chipsErrors);
                }

                return data;
            } catch (error) {
                return [];
            }
        }

        throw Error('Invalid load-data method');
    }

    private getDecodedRow<TType extends GridRow = GridRow>(row: TType, encodedColumns: string[]): TType {
        for (let column of encodedColumns) {
            let decoded = '';

            try {
                if (!!row[column]?.trim()) decoded = this.base64.urlDecode(row[column]);
            } catch (error) {
                decoded = row[column];
                console.log('Failed to decode column >>:', column, '. \n Encoded value kept >>: ', row[column]);
                console.error(error);
            }

            (row as GridRow)[column] = decoded;
        }

        return row;
    }

    /**
     * Retrieves row data sending in additional parameters for server-side paging, filtering and sorting. Returns object containing items retrieved and total items available.
     *
     * @param method the load data method for retrieving grid row data.
     * @param params the parameters required for retrieving the row data, if any.
     * @param serverSideParams parameters to use for server-side paging, filtering and sorting.
     * @returns promise containing an object with list of data and the total row count.
     */
    public async getGridDataServerSide<TType extends GridRow = GridRow>(method: number, params: any, serverSideParams: ServerSideRequestParams): Promise<{ items: TType[]; total: number }> {
        if (!!method && !isNaN(Number(method)) && !!serverSideParams) {
            // @ts-ignore datatask options does actually support partial parameters.
            const data = await this.datatask.Post(method, { ...(params || {}), ttGridOptions: serverSideParams }, { returnRawResponse: true });

            return data;
        }

        throw Error('Invalid parameters');
    }

    /**
     * Retrieves list of grid column layouts attached to the grid with the given load data method.
     *
     * @param loadDataMethod the load data method of the grid to get column layouts for.
     * @returns a promise containing a list of grid column layouts.
     */
    public getColumnLayouts(loadDataMethod: number): Promise<GridColumnLayout[]> {
        if (loadDataMethod) {
            return this.datatask.Post(2240, { p2_datatask_keyno: loadDataMethod });
        }

        throw Error('Missing load data method');
    }

    /**
     * Creates a column layout.
     *
     * @param params the column layout to create.
     * @returns a promise returning errorcode '0' if nothing went wrong.
     */
    public async createColumnLayout(params: CreateColumnLayoutParams): Promise<{ errorcode: string; errormessage: string; gridlayout_keyno: string }> {
        if (params && !isNaN(Number(params.loadDataMethod)) && params.layoutname?.trim() && params.layout && typeof params.layout === 'object') {
            return (
                await this.datatask.Post(2241, {
                    gridlayout_keyno: '',
                    p2_datatask_keyno: params.loadDataMethod,
                    gridlayout_name: params.layoutname,
                    layout_schema: this.utoa(JSON.stringify(params.layout)),
                    orderby: 0,
                    function: 'NEW',
                    is_base64: 1,
                    view_id: params.rememberId,
                    is_std: params.is_std,
                })
            )[0];
        }

        throw Error('Invalid parameters');
    }

    /**
     * Updates the given layout with the changes it contains.
     *
     * @param layout the layout to update.
     * @returns a promise returning errorcode '0' if nothing went wrong.
     */
    public updateColumnLayout(layout: GridColumnLayout, rememberId: string): Promise<{ errorcode: string; errormessage: string }> {
        if (layout.gridlayout_name?.trim() && layout.gridlayout_keyno?.trim() && layout.layout_schema?.trim()) {
            return this.datatask.Post(2241, {
                function: 'UPDATE',
                gridlayout_keyno: layout.gridlayout_keyno,
                gridlayout_name: layout.gridlayout_name,
                layout_schema: layout.layout_schema,
                orderby: layout.orderby,
                view_id: rememberId,
                p2_datatask_keyno: layout.p2_datatask_keyno,
                is_std: layout.is_std,
                is_base64: 1,
            });
        }

        throw Error('Invalid parameters');
    }

    /**
     * Deletes the given column layout.
     *
     * @param layout the column layout to delete.
     * @returns a promise returning errorcode '0' if nothing went wrong.
     */
    public deleteColumnLayout(layout: GridColumnLayout, rememberId: string): Promise<{ errorcode: string; errormessage: string }> {
        if (layout?.gridlayout_keyno) {
            return this.datatask.Post(2241, {
                function: 'DELETE',
                gridlayout_keyno: layout.gridlayout_keyno,
                p2_datatask_keyno: layout.p2_datatask_keyno,
                view_id: rememberId,
            });
        }

        throw Error('Invalid parameters');
    }

    /**
     * Performs a look up search for the given method, searchfield, searchtext and grid row.
     *
     * @param method the look up method to use.
     * @param searchfield the field searching from.
     * @param searchtext the search text user typed.
     * @param row the grid row the search was called from.
     * @returns a promise containg a list of objects as search results.
     */
    public lookupSearch(method: number, searchfield: string, searchtext: string, row: GridRow): Promise<{ [key: string]: string }[]> {
        return this.datatask.Post(method, { searchfield: searchfield, searchtext: searchtext, row: row });
    }

    /**
     * Retrieves goto parameters for the given set of parameters.
     *
     * @param params the parameters used to retrieve goto parameters for.
     * @returns an object containing the goto parameters.
     */
    public async getGotoParms(params: GridGotoParmsParams): Promise<GridGotoParmsResponse> {
        const parameters: {
            datatask?: DataTaskRequest | null;
            field: string;
            row?: GridRow | null;
            gridRemId?: string | null;
            colgroup?: string | null;
            rowkey?: string | null;
        } = {
            datatask: params.loadDataTask,
            field: params.field,
            row: params.row,
            gridRemId: params.rememberId,
        };

        if (params.colgroup !== undefined) {
            parameters.colgroup = params.colgroup;
        }

        if (params.rowkey !== undefined) {
            parameters.rowkey = params.rowkey;
        }

        return (await this.datatask.Post(2837, parameters))[0] ?? { item_state: '', item_parms: '', item_path: '' };
    }

    /**
     * Retrieves the procedure name for the given load data method.
     *
     * @param loadDataMethod the load data method to retrieve.
     * @returns the proc name retrieved.
     */
    public async getLoadMethodName(loadDataMethod: number): Promise<string> {
        if (!!loadDataMethod && !isNaN(Number(loadDataMethod))) {
            // @ts-ignore
            const data = await this.datatask.Post(2530, { p2_datatask_keyno: loadDataMethod }, { returnRawResponse: true });
            return (data?.proc_name as string) || '';
        }

        throw Error('Invalid loadmethod for receiving load method name.');
    }

    // /**
    //  * Saves the changes of the given change state.
    //  *
    //  * @param state which state to save.
    //  * @returns empty promise which fullfills once the changes are persisted.
    //  */
    // public async saveChanges(state: ChangeState, options: GridOptions, gridApi: GridApi | undefined): Promise<unknown> {
    //     const persistedChangeKeys: string[] = [];

    //     try {
    //         return await this.customSave(options, state);
    //     } catch (error) {
    //         const dataTaskId = this.getDataTaskId(state);

    //         if (dataTaskId === null || options.dataTask?.[dataTaskId]?.method instanceof Function) return;

    //         if (options.dataTask?.saveData?.confirm === true) {
    //             const result = await this.modalService.openConfirmDialog({ type: 'warning', title: 'ttgrid_modal_save_title', message: 'ttgrid_modal_save_message', ok: 'ttgrid_modal_save_ok', cancel: 'ttgrid_modal_save_cancel' });

    //             if (result !== true) return;
    //         }

    //         this.validateStateMethod(state, options);

    //         const rowsToSave = Object.entries(options.data!.changes!).filter(([_, value]) => value.state === state);

    //         const promises = rowsToSave.map(async ([key, value]) => {
    //             try {
    //                 persistedChangeKeys.push(key);
    //                 //const response = { key: key, ...(await this.saveSingleRow({ state: state, row: value.data, options: options, refreshRow: false, gridApi: gridApi })) };
    //                 const response = { key: key, ...(await this.saveSingleRow({ state: state, row: Object.assign({}, value.data), options: options, refreshRow: false, gridApi: gridApi, event: value.event, rowIndex: value.rowIndex })) };
    //                 return response;
    //             } catch (saveError) {
    //                 console.error(saveError);
    //             } finally {
    //                 // delete options.data?.changes?.[value.data._uuid!];
    //             }
    //         });

    //         const response: { key: string; rowIndex?: number | null; errorcode?: number; errormessage?: string; savedata?: any }[] = await Promise.all(promises);

    //         const errors: { key: string; rowIndex?: number | null; errorcode?: number; errormessage?: string }[] = response.filter((error) => (error?.errorcode ?? 0) < 0);
    //         const uniqueMessages = new Set(errors.map((error) => error.errormessage).filter((message) => !!message?.trim?.()));
    //         const messagesWithCount = Array.from(uniqueMessages).map((message) => ({ count: errors.filter((error) => error.errormessage === message).length, message: message }));
    //         const message = messagesWithCount.reduce((acc, msg) => (acc += `\n${msg.message}${msg.count > 1 ? ` (${msg.count})` : ''}`), '');

    //         if (state === 'remove') {
    //             for (let data of response) {
    //                 if ((data?.errorcode ?? 0) < 0) {
    //                     const changeData: GridRowChanges | undefined = options.data?.changes?.[data.key];

    //                     if (!!changeData && !options.data?.rowData.find((row) => row._uuid === data.key)) options.data?.rowData.splice(0, 0, { ...changeData.data, ...(data?.savedata ?? {}), _saveFailed: true });
    //                 }
    //             }
    //         } else {
    //             for (let data of response) {
    //                 if ((data?.errorcode ?? 0) !== 0) {
    //                     console.log(data);
    //                     const changeData: GridRowChanges | undefined = options.data?.changes?.[data.key];
    //                     const updatedRow = options.data?.rowData.find((row) => row._uuid === data.key);

    //                     if (!!changeData && !!updatedRow) {
    //                         updatedRow['_saveFailed'] = true;
    //                     }

    //                     if (!!updatedRow && !!data.savedata && Object.keys(data.savedata).length > 0) {
    //                         Object.assign(updatedRow, data.savedata);
    //                         // updatedRow = {...updatedRow, ...data.savedata}
    //                     }
    //                 }
    //             }
    //         }

    //         if (errors.length > 0) {
    //             gridApi?.setGridOption('rowData', options.data?.rowData);

    //             throw Error(message);
    //         }

    //         return response;
    //     } finally {
    //         persistedChangeKeys.forEach((key) => delete options.data!.changes![key]);
    //     }
    // }

    public async saveAllChanges<TType extends GridRow = GridRow>(options: GridOptions<TType>, progressModal: ProgressModalComponent | null = null, gridApi: GridApi | undefined) {
        if (Object.keys(options.data!.changes!).length < 1) return;

        let count = 0;
        const responses: (SaveRowResponse<TType> | null | undefined)[] = [];
        const rowsToSave = Object.entries(options.data!.changes!).filter(([_, value]) => {
            if (options.dataTask!.saveData!.onlySaveIsSelected === true) {
                return value.data?.['is_selected'] === true;
            } else {
                return true;
            }
        });

        if (progressModal) {
            progressModal.data.max = rowsToSave.length;
        }

        if (options.dataTask?.saveData?.single === true) {
            let error: any = null;

            // for .. of loop are a part of the function scope they reside in, so each iteration will be waited for before next is executed.
            for (let [_, value] of rowsToSave) {
                responses.push(await this.saveSingleRowAndUpdateProgress({ options: options, value: value, progressModal: progressModal, gridApi: gridApi, count: count, error: this.getErrorInfo(error) }));

                if (progressModal && progressModal.data.status === 'cancelled') {
                    break;
                }

                count++;
            }

            if (error?.error !== true && progressModal) {
                progressModal?.updateStatus('finish');
            }
        } else {
            const errors: string[] = [];
            // maps the changes to promises, no waiting here.
            const promises = this.mapRowChangesToSavePromises({ options: options, rowChanges: rowsToSave, progressModal: progressModal, count: count, errors: errors, gridApi: gridApi });

            const results = await Promise.allSettled(promises);

            responses.push(...results.map((result) => (result.status === 'fulfilled' ? result.value : null)));

            if (errors.length > 0) {
                if (progressModal) {
                    progressModal.updateStatus('invalid');
                    progressModal.updateInvalidText(errors.reduce((message, error) => message + '\n' + error));
                } else {
                    throw Error(errors[0]);
                }
            } else if (progressModal) {
                progressModal.updateStatus('finish');
            }
        }

        if (options.dataTask?.saveData?.readAfterSave !== true) {
            rowsToSave.forEach((row) => {
                delete options.data!.changes![row[0]];
            });
        } else {
            options.data!.changes = {};
        }

        this.addGridMessagesFromSaveResponses(responses);

        return responses;
    }

    private async saveSingleRowAndUpdateProgress<TType extends GridRow = GridRow>({
        options,
        value,
        count,
        progressModal,
        gridApi,
        error,
    }: {
        options: GridOptions<TType>;
        value: GridRowChanges;
        count: number;
        progressModal: ProgressModalComponent | null;
        gridApi: GridApi | undefined;
        error: {
            error: boolean;
            code?: any;
            message?: any;
        };
    }) {
        const response = await this.saveSingleRow({ state: value.state, row: value.data, options: options, gridApi: gridApi, refreshRow: true, event: value.event, rowIndex: value.rowIndex, rowIdOver: value.rowOverId, direction: value.direction });

        error = this.getErrorInfo(response);

        if (error?.error === true) {
            if (progressModal) {
                progressModal?.updateInvalidText(this.getErrorInfo(response).message);
                progressModal?.updateStatus('invalid');
            }

            throw Error(error?.message);
        } else if (progressModal) {
            count++;
            progressModal?.updateValue(count);
        }

        return response;
    }

    private mapRowChangesToSavePromises<TType extends GridRow = GridRow>({ options, rowChanges, progressModal, count, errors, gridApi }: { options: GridOptions<TType>; rowChanges: [string, GridRowChanges][]; progressModal: ProgressModalComponent | null; gridApi: GridApi | undefined; count: number; errors: string[] }) {
        return rowChanges.map(async ([_, value]) => {
            const response = await this.saveSingleRow({ state: value.state, row: value.data, options: options, gridApi: gridApi, refreshRow: true, event: value.event, rowIndex: value.rowIndex, rowIdOver: value.rowOverId, direction: value.direction });
            const error = this.getErrorInfo(response);

            if (error?.error === true) {
                errors.push(error?.message);
            } else {
                count++;

                if (progressModal) {
                    progressModal.updateValue(count);
                }
            }

            return response;
        });
    }

    private validateStateMethod<TType extends GridRow = GridRow>(state: ChangeState, options: GridOptions<TType>) {
        const dataTaskId = this.getDataTaskId(state);

        if (dataTaskId === null) return;

        if (!options.dataTask || typeof options.dataTask !== 'object') {
            options.dataTask = {};
        }

        options.dataTask[dataTaskId] ??= { method: 2219, parameters: {} };
        options.dataTask[dataTaskId]!.parameters ??= {};

        if (!(options.dataTask[dataTaskId]?.method instanceof Function)) {
            if (!!options.dataTask[dataTaskId]?.method && !isNaN(Number(options.dataTask[dataTaskId]?.method))) {
                options.dataTask[dataTaskId]!.method = parseInt(`${options.dataTask[dataTaskId]?.method}`, 10);
            } else {
                options.dataTask[dataTaskId]!.method = 2219;
            }
        }
    }

    public async getParametersFromDataTaskId<TType extends GridRow = GridRow>(options: GridOptions<TType>, datataskId: keyof GridDataTask) {
        const datataskConfig = options.dataTask?.[datataskId];
        let parameters = {};

        if (!datataskConfig || typeof datataskConfig === 'string' || !datataskConfig.parameters) return parameters;

        if (datataskConfig.parameters instanceof Function) {
            const result = datataskConfig.parameters();

            if (result instanceof Promise) {
                parameters = { ...(await result) };
            } else {
                parameters = { ...result };
            }
        } else {
            parameters = { ...datataskConfig.parameters, ...parameters };
        }

        return parameters;
    }

    public async customSave<TType extends GridRow = GridRow>(gridOptions: GridOptions<TType>, state: ChangeState) {
        const datataskId = this.getDataTaskId(state);
        const customSaveFunction = datataskId === null ? null : gridOptions?.dataTask?.[datataskId]?.method;

        if (!!customSaveFunction && customSaveFunction instanceof Function) {
            let result = customSaveFunction(state, gridOptions.data?.changes || {});

            if (result instanceof Function) {
                result = result(state, gridOptions.data?.changes || {});
            }

            if (result instanceof Promise) {
                result = await result;
            }

            return result;
        } else {
            throw Error(state + ' method is not a function, could not perform custom-save');
        }
    }

    //     tryCustomSave(state).then(function (hasCustomSave) {
    //         if (hasCustomSave === true) {
    //             angular.forEach(changes, function (value, key) {
    //                 if (value.state === state) {
    //                     delChanges.push(key);
    //                 }
    //             });

    //             autoSaveCompleted();
    //         } else {
    //             validateMethod(state);

    //             var promises = [];

    //             var dataTaskId = getDataTaskId(state);

    //             // doing forEach here but since this method is only meant for autoSave there should never
    //             // be more than one record of the selected state.
    //             angular.forEach(changes, function (value, key) {
    //                 if (value.state === state) {
    //                     var parameters = vm.ttOptions.dataTask[dataTaskId].parameters;

    //                     if (angular.isUndefined(parameters) || parameters === null || parameters === '') {
    //                         parameters = {};
    //                     }

    //                     // BJS 20221125 - Fix for datetime offset.
    //                     let tzo = (new Date()).getTimezoneOffset() * 60000; //offset in milliseconds

    //                     angular.forEach(value.data, function (d, key) {
    //                         if (angular.isDefined(columnInfo[key]) && columnInfo[key].type === 'date') {
    //                             value.data[key] = moment((new Date(new Date(d) - tzo))).format('YYYY-MM-DD HH:mm');
    //                         }
    //                     });

    //                     parameters.state = state;
    //                     parameters.changes = value.data;
    //                     parameters.load_datatask_keyno = getLoadDataMethod();

    //                     promises.push($ihttp.post({ method: vm.ttOptions.dataTask[dataTaskId].method, parameters: parameters }));

    //                     delChanges.push(key);
    //                 }
    //             });

    //             $q.all(promises).then(function (responses) {
    //                 var msg = '';
    //                 var hasError = false;

    //                 angular.forEach(responses, function (response) {
    //                     if (angular.isDefined(response)) {
    //                         var info = getErrorInfo(response);

    //                         if (info.error === true) {
    //                             msg += info.msg + '\n\r';

    //                             hasError = true;
    //                         }
    //                     }
    //                 });

    //                 if (hasError === true) {
    //                     showErrorMessage(0, msg).then(function () {
    //                         autoSaveCompleted(responses)
    //                     });
    //                 } else {
    //                     autoSaveCompleted(responses);
    //                 }
    //             });
    //         }
    //     });
    // }

    /**
     * Returns the key name for the datatask configuration required by the given state.
     *
     * @param state the state to get the keyname of datatask configuration for.
     * @returns the key name for the datatask configuration or `null` if state not supported.
     */
    private getDataTaskId(state: ChangeState): 'addRow' | 'saveData' | 'removeRow' | null {
        let dataTaskId: keyof GridDataTask | null = null;

        switch (state) {
            case 'add':
                dataTaskId = 'addRow';
                break;
            case 'update':
            case 'all':
            case 'read':
                dataTaskId = 'saveData';
                break;
            case 'remove':
                dataTaskId = 'removeRow';
                break;
            default:
                break;
        }

        return dataTaskId;
    }

    /**
     * Saves the changes of the given row using the given state.
     *
     * @param state the change state to save the changes with.
     * @param row the row to save changes on.
     * @returns empty promise which fullfills when the change is persisted.
     */
    public async saveSingleRow<TType extends GridRow = GridRow>({ state, row, options, refreshRow = true, gridApi, event, rowIndex, rowIdOver, direction }: { state: ChangeState; row: GridRow; event: string; rowIndex: number | null; rowIdOver: string | null; direction: string | null; options: GridOptions<TType>; refreshRow: boolean; gridApi: GridApi | undefined }) {
        try {
            const dataTaskId = this.getDataTaskId(state);

            if (dataTaskId === null || !options.dataTask?.[dataTaskId]?.method || options.dataTask?.[dataTaskId]?.method instanceof Function) return;

            this.serializeRow(row, options);

            let parameters = await this.getParametersFromDataTaskId(options, dataTaskId);

            if (options.dataTask.saveData?.saveInclLoadparms === true) {
                parameters = { ...(await this.getParametersFromDataTaskId(options, 'loadData')), ...parameters };
            }

            let rowdata = this.getRowData(options, row);

            // BJS 20250324 - Sometimes we need a unique id for a row before the keyno is created in the database. If a column named
            //                tt_gridrow_id exists in the rowdata then the value will be set if it does not exist.
            //                This is for instance used when inserting new orderlines because sometimes there is no time to wait for the first
            //                save to return with the keyno before the next save runs. If the update procedure detects there is no orderline_keyno set
            //                but there is a tt_gridrow_id (wich is generated at the client) then it checks if there is an orderline with this id. If there
            //                is no orderline with this id it assumes this is a new orderline. If it finds an orderline with matching tt_gridrow_id then
            //                that row will be updated.
            if (rowdata['tt_gridrow_id'] !== undefined && (rowdata['tt_gridrow_id'] === null || rowdata['tt_gridrow_id'].length === 0)) {
                rowdata['tt_gridrow_id'] = row._uuid;
            }

            let dirtyCells: string[];

            // BJS 20250410
            if (row?._uuid && options?.data?.dirtyRows[row._uuid]) {
                dirtyCells = options?.data?.dirtyRows[row._uuid].cells;

                delete options.data.dirtyRows[row._uuid];
            } else {
                dirtyCells = [];
            }

            // BJS 20250410
            const distinctDirtyCells = Array.from(new Set(dirtyCells));

            // BJS 20250409 - Added dirtyCells parameter
            // @ts-ignore
            const response = await this.datatask.Post(options.dataTask![dataTaskId]!.method as number, { ...parameters, state: state, changes: rowdata, load_datatask_keyno: options.dataTask?.loadData?.method, dirtyCells: distinctDirtyCells.join(','), event: event, rowIndex: rowIndex, rowIdOver: rowIdOver, direction: direction }, { returnRawResponse: true });

            if (response?.read === 1) {
                await options.gridfunc?.read(false);
            }

            const encodedColumns = Object.entries(options?.data?.columnInfo ?? {})
                .filter(([_, info]) => info?.base64 === true)
                .map(([colname, _]) => colname);

            if (!!response.savedata) {
                response.savedata = this.getDecodedRow(response.savedata, encodedColumns);

                if ('_uuid' in response.savedata) {
                    delete response.savedata._uuid; // immutable
                }
            }

            const rowToUpdate = options.data?.rowData.find((rowItem) => row._uuid === rowItem._uuid);

            if (options.dataTask.saveData?.readAfterAdd === true && state === 'add') {
                options.gridfunc?.gridProgress(true);
                await options.gridfunc?.read();
                options.gridfunc?.gridProgress(false);
            } else if (options.dataTask.saveData?.refreshRowOnSave && rowToUpdate && !!response?.savedata && refreshRow === true) {
                options.data!.rowData[options.data!.rowData.indexOf(rowToUpdate)] = { ...rowToUpdate, ...response.savedata, _dirty: false };
                const node = gridApi?.getRowNode(row._uuid!);
                node?.setData({ ...rowToUpdate, ...response.savedata, _dirty: false })!;
            } else if (!!rowToUpdate) {
                const node = gridApi?.getRowNode(row._uuid!);

                if (node) {
                    options.data!.rowData[options.data!.rowData.indexOf(rowToUpdate)] = { ...rowToUpdate, _dirty: false };
                    node.updateData({ ...node.data, _dirty: false })!;
                }
            }

            return { ...response, row: rowToUpdate };
        } catch (error) {
            const message = `${error}`;

            if (!message.includes('"errorcode":0')) throw error;

            if (options.dataTask?.saveData?.readAfterAdd === true && state === 'add') {
                options.gridfunc?.read();
            } else {
                const rowToUpdate = options.data?.rowData.find((rowItem) => row._uuid === rowItem._uuid);

                if (!!rowToUpdate) {
                    const node = gridApi?.getRowNode(row._uuid!);

                    if (node) {
                        options.data!.rowData[options.data!.rowData.indexOf(rowToUpdate)] = { ...rowToUpdate, _dirty: false };
                        node.updateData({ ...node.data, _dirty: false })!;
                    }
                }
            }
        }
    }

    /**
     * Saves the changes of the given change state.
     *
     * @param state which state to save.
     * @returns empty promise which fullfills once the changes are persisted.
     */
    public async newSaveChanges<TType extends GridRow = GridRow>(state: ChangeState, options: GridOptions<TType>) {
        try {
            return await this.customSave(options, state);
        } catch (error) {
            return await this.standardSaveChanges(state, options);
        }
    }

    public async confirmSave<TType extends GridRow = GridRow>(options: GridOptions<TType>): Promise<void> {
        if (options.dataTask?.saveData?.confirm === true) {
            const result = await this.modalService.openConfirmDialog({ type: 'warning', title: 'ttgrid_modal_save_title', message: 'ttgrid_modal_save_message', ok: 'ttgrid_modal_save_ok', cancel: 'ttgrid_modal_save_cancel' });

            if (result !== true) throw Error('User did not confirm save');
        }
    }

    public getChangesToSaveWithRowNode<TType extends GridRow = GridRow>(state: ChangeState, options: GridOptions<TType>, params?: SaveChangesParams) {
        return Object.entries(options.data!.changes!)
            .filter(([_, value]) => value.state === state)
            .filter(([key]) => !params?.rowIds || params.rowIds.includes(key))
            .map((record) => {
                const change = record[1];
                const node = options.gridfunc?.getRowNode(change.data as TType);

                return { ...change, data: (node?.data ?? change.data) as TType, rowIndex: node?.rowIndex ?? change.rowIndex, changeKey: record[0], changeRef: change, node };
            });
    }

    public async standardSaveChanges<TType extends GridRow = GridRow>(state: ChangeState, options: GridOptions<TType>, params?: SaveChangesParams) {
        const persistedChanges = new Map<string, GridRowChanges<TType>>();
        const { method, parameters } = await this.getSaveFunction(state, options);
        await this.confirmSave(options);

        try {
            const saveRow = async (change: GridRowChanges<TType> & { changeKey: string; changeRef: GridRowChanges<TType>; node: IRowNode | null | undefined }) => {
                try {
                    persistedChanges.set(change.changeKey, change.changeRef);

                    return await this.saveRow<TType>({ method, saveParameters: parameters, rowIndex: change.rowIndex, event: change.event, data: change.data, node: change.node!, state: change.state, options: options, rowIdOver: change.rowOverId, direction: change.direction });
                } catch (error) {
                    console.error(error);
                    return null;
                }
            };

            const changes = this.getChangesToSaveWithRowNode<TType>(state, options, params);
            let responses: (SaveRowResponse<TType> | null)[] = [];
            let progressModal: ProgressModalComponent | null = null;
            let count = 0;
            if (params?.progress?.show) {
                progressModal = this.modalService.openProgressDialog({ value: count, max: changes.length, finishclose: true, rememberId: options.dataTask?.rememberId + 'grid.autosave.progressmodal', ...params.progress.progressModalData });
            }

            if (params?.await === true) {
                for (let change of changes) {
                    responses.push(await saveRow(change));
                    count++;
                    progressModal?.updateValue(count);
                }
            } else {
                progressModal?.updateIndeterminate(true);
                responses = await Promise.all(changes.map(saveRow));
            }

            progressModal?.updateStatus('finish');

            let shouldReadGrid = false;

            if (responses.some((response) => response?.read === 1)) {
                shouldReadGrid = true;
            }

            this.addGridMessagesFromSaveResponses(responses);

            const { errors, message } = this.getErrors(responses);

            console.log(errors);

            if (state === 'remove') {
                this.addFailedDeletedRowsBackToGrid<TType>(errors, options);
            } else {
                this.markFailedSavedRowsAndUpdateData(errors, options);
            }

            this.updateRowsFromSaveResponses(responses, options);

            if (shouldReadGrid) {
                await options.gridfunc?.read(false);
            } else if (errors.length > 0) {
                options.gridfunc?.getGridApi()?.setGridOption('rowData', options.data?.rowData);

                throw Error(message);
            }

            return responses;
        } finally {
            persistedChanges.forEach((changeRef, key) => {
                if (options.data!.changes![key] === changeRef) {
                    delete options.data!.changes![key];
                }
            });
        }
    }

    private gridMessages$ = new Subject<GridMessageQueueItem>();

    public addGridMessage(messages: GridMessage[]): void {
        const queuedMessages: GridMessageQueueItem[] = [];
        const modalGroups = new Map<GridMessageType, GridMessage[]>();

        for (const message of messages) {
            if (this.getGridMessageDisplayType(message) === 'modal') {
                const type = this.getGridMessageType(message);
                let group = modalGroups.get(type);

                if (!group) {
                    group = [];
                    modalGroups.set(type, group);
                    queuedMessages.push(group);
                }

                group.push(message);
            } else {
                queuedMessages.push(message);
            }
        }

        queuedMessages.forEach((message) => this.gridMessages$.next(message));
    }

    private addGridMessagesFromSaveResponses(responses: ({ messages?: GridMessage[] | null } | null | undefined)[]): void {
        this.addGridMessage(responses.map((response) => response?.messages ?? []).flat());
    }

    private openGridMessage(message: GridMessageQueueItem): Observable<unknown> {
        if (Array.isArray(message)) {
            return this.openGridMessagesModal(message);
        }

        if (this.getGridMessageDisplayType(message) === 'toast') {
            return from(
                this.toast.addToast({
                    id: message.id,
                    title: message.title,
                    translateTitle: false,
                    message: message.message,
                    translateMessage: false,
                    hideDelay: message.duration,
                    type: message.type ?? 'warning',
                    onClick: !!message.item_state
                        ? ({ toast, event }) => {
                              this.toast.animateHide(toast, 1000, 'running');

                              if (!message.item_state) return;

                              if (this.utility.isModifierKeyPressed(event)) {
                                  this.state.newTab(message.item_state, message.item_parms);
                              } else {
                                  this.state.go(message.item_state, message.item_parms);
                              }
                          }
                        : undefined,
                }),
            );
        }

        if (this.getGridMessageDisplayType(message) === 'modal') {
            return this.openGridMessagesModal([message]);
        }

        return EMPTY;
    }

    private openGridMessagesModal(messages: GridMessage[]): Observable<unknown> {
        if (messages.length < 1) return EMPTY;

        const type = this.getGridMessageType(messages[0]);
        const title = messages.find((message) => !!message.title?.trim?.())?.title ?? ' ';

        return this.dialog
            .open<GridMessagesModalComponent, GridMessagesModalData>(GridMessagesModalComponent, {
                width: '500px',
                maxWidth: '90vw',
                data: {
                    type,
                    title,
                    messages,
                },
            })
            .afterClosed();
    }

    private getGridMessageDisplayType(message: GridMessage): string {
        return `${message.display_type || 'toast'}`.trim().toLowerCase();
    }

    private getGridMessageType(message: GridMessage): GridMessageType {
        return message.type ?? 'warning';
    }

    private updateRowsFromSaveResponses<TType extends GridRow = GridRow>(responses: (SaveRowResponse | null)[], options: GridOptions<TType>) {
        for (let response of responses) {
            if (!!response && typeof response === 'object' && 'savedata' in response && !!response?.savedata && !!response.node) {
                const rowIndex = options.data?.rowData.findIndex((r) => r._uuid === response!.data._uuid);

                if (rowIndex !== undefined && rowIndex !== -1) {
                    const savedata = this.getSaveDataWithoutDirtyCells(response, options);
                    const rowId = response.node.id ?? response.data?._uuid;

                    options.data!.rowData[rowIndex] = { ...options.data!.rowData[rowIndex], ...savedata } as TType;
                    response.node.setData({ ...options.data!.rowData[rowIndex] });

                    if (rowId && options.data?.changes?.[rowId]) {
                        options.data.changes[rowId].data = options.data.rowData[rowIndex];
                    }
                }
            }
        }

        const rowNode = options.gridfunc?.getRowAt(options.gridfunc.getGridApi()?.getFocusedCell()?.rowIndex!);

        if (rowNode) {
            updateActiveCellEditorFromGridRow(options);
        }
    }

    private addFailedDeletedRowsBackToGrid<TType extends GridRow = GridRow>(failedSaves: (SaveRowResponse<TType> | null)[], options: GridOptions<TType>) {
        for (let data of failedSaves) {
            if (!!data && typeof data === 'object' && 'errorcode' in data && (data?.errorcode ?? 0) < 0) {
                const changeData: GridRowChanges | undefined = options.data?.changes?.[data!.data._uuid!];

                if (!!changeData && !options.data?.rowData.find((row) => row._uuid === data!.data._uuid!)) options.data?.rowData.splice(0, 0, { ...changeData.data, ...(data?.savedata ?? {}), _saveFailed: true } as unknown as TType);
            }
        }
    }

    private markFailedSavedRowsAndUpdateData<TType extends GridRow = GridRow>(failedSaves: (SaveRowResponse | null)[], options: GridOptions<TType>) {
        for (let data of failedSaves) {
            console.log(data);
            if (!!data && typeof data === 'object' && 'errorcode' in data && (data?.errorcode ?? 0) !== 0) {
                console.log('valid data');
                const changeData: GridRowChanges | undefined = options.data?.changes?.[data!.node!.id!];
                const updatedRow: GridRow | undefined = options.data?.rowData.find((row) => row._uuid === data!.node!.id!);

                if (!!changeData && !!updatedRow) {
                    console.log('setSaveFailed');
                    updatedRow['_saveFailed'] = true;
                }

                if (!!updatedRow && !!data.savedata && Object.keys(data.savedata).length > 0) {
                    Object.assign(updatedRow, this.getSaveDataWithoutDirtyCells(data, options));
                }
            }
        }
    }

    private getSaveDataWithoutDirtyCells<TType extends GridRow = GridRow>(response: SaveRowResponse | null, options: GridOptions<TType>): Partial<TType> {
        if (!response?.savedata) return {};

        const savedata = { ...response.savedata } as Partial<TType>;
        const rowId = response.node?.id ?? response.data?._uuid;
        const dirtyCells = rowId ? (options.data?.dirtyRows[rowId]?.cells ?? []) : [];

        for (const dirtyCell of dirtyCells) {
            delete savedata[dirtyCell as keyof TType];
        }

        return savedata;
    }

    private getErrors(responses: any[]): { errors: any[]; message: string } {
        const errors = responses.filter((error) => (error?.errorcode ?? 0) < 0);
        const uniqueMessages = new Set(errors.map((error) => error.errormessage).filter((message) => !!message?.trim?.()));
        const messagesWithCount = Array.from(uniqueMessages).map((message) => ({ count: errors.filter((error) => error.errormessage === message).length, message: message }));
        const message = messagesWithCount.reduce((acc, msg) => (acc += `\n${msg.message}${msg.count > 1 ? ` (${msg.count})` : ''}`), '');

        return { errors, message };
    }

    public async saveRow<TType extends GridRow = GridRow>(params: SaveRowParams<TType>): Promise<SaveRowResponse<TType> | null> {
        try {
            // const { method, parameters } = await this.getSaveFunction(params.parameters.state, params.options);

            this.serializeRow(params.data, params.options);

            const response = await this._saveRow({
                method: params.method,
                parameters: {
                    ...params.saveParameters,
                    state: params.state,
                    event: params.event,
                    load_datatask_keyno: +params.options.dataTask?.loadData?.method!,
                    rowIndex: params.rowIndex,
                    dirtyCells: this.getDirtyCells(params.data, params.options),
                    changes: params.data,
                    direction: params.direction,
                    rowIdOver: params.rowIdOver,
                },
            });

            const savedata = this.getDecodedSaveData(response, params);

            // params.node.updateData({ ...params.node.data, ...savedata });
            return { ...response, data: params.data, node: params.node };
        } catch (error) {
            this.handleSaveRowError(error, params);
            return null;
        }
    }

    private getDecodedSaveData<TType extends GridRow = GridRow>(response: _SaveRowResponse, params: SaveRowParams<TType>) {
        const encodedColumns = Object.entries(params.options?.data?.columnInfo ?? {})
            .filter(([_, info]) => info?.base64 === true)
            .map(([colname, _]) => colname);

        if (!!response.savedata) {
            response.savedata = this.getDecodedRow(response.savedata, encodedColumns);

            if ('_uuid' in response.savedata) {
                delete response.savedata._uuid; // immutable
            }
        }

        return response.savedata;
    }

    private handleSaveRowError<TType extends GridRow = GridRow>(error: any, params: SaveRowParams<TType>) {
        console.error(error);
        const message = `${error}`;

        if (!message.includes('"errorcode":0')) throw error;

        if (params.options.dataTask?.saveData?.readAfterAdd === true && params.state === 'add') {
            params.options.gridfunc?.read();
        } else {
            const rowToUpdate = params.options.data?.rowData.find((rowItem) => params.data._uuid === rowItem._uuid);

            if (!!rowToUpdate) {
                params.node?.updateData({ ...params.node.data, ...rowToUpdate, _dirty: false })!;
            }
        }
    }

    // const response = await this.datatask.Post(options.dataTask![dataTaskId]!.method as number, { ...parameters, state: state, changes: rowdata, load_datatask_keyno: options.dataTask?.loadData?.method, dirtyCells: distinctDirtyCells.join(','), event: event, rowIndex: rowIndex }, { returnRawResponse: true });
    public async getSaveFunction<TType extends GridRow = GridRow>(state: ChangeState, options: GridOptions<TType>): Promise<{ method: number; parameters: any }> {
        const dataTaskId = this.getDataTaskId(state);

        if (dataTaskId === null || !options.dataTask?.[dataTaskId]?.method || options.dataTask?.[dataTaskId]?.method instanceof Function) throw Error('invalid method for save function with state: ' + state);

        this.validateStateMethod(state, options);

        const method = +options!.dataTask![dataTaskId]!.method!;

        let parameters = await this.getParametersFromDataTaskId(options, dataTaskId);

        if (options.dataTask.saveData?.saveInclLoadparms === true) {
            parameters = { ...(await this.getParametersFromDataTaskId(options, 'loadData')), ...parameters };
        }

        return { method, parameters };
    }

    private getDirtyCells<TType extends GridRow = GridRow>(row: GridRow, options: GridOptions<TType>): string {
        let dirtyCells: string[];

        // BJS 20250410
        if (row?._uuid && options?.data?.dirtyRows[row._uuid]) {
            dirtyCells = options?.data?.dirtyRows[row._uuid].cells;

            delete options.data.dirtyRows[row._uuid];
        } else {
            dirtyCells = [];
        }

        // BJS 20250410
        const distinctDirtyCells = Array.from(new Set(dirtyCells));
        return distinctDirtyCells.join(',');
    }

    private async _saveRow(params: _SaveRowParams): Promise<_SaveRowResponse> {
        let response: _SaveRowResponse | null = null;

        try {
            response = await this.datatask.Post(+params.method, params.parameters, { returnRawResponse: true, suppressErrorMessage: true });
        } catch (error) {
            console.dir(error);
            response = (error as DataTaskError).response?.data;
        }

        // @ts-ignore
        return response as _SaveRowResponse;
    }

    /**
     * Maps the data of the given row to db friendly formats.
     *
     * @param row the row to format its data to db friendly formats for.
     */
    private serializeRow<TType extends GridRow = GridRow>(row: GridRow, options: GridOptions<TType>) {
        for (let [colname, { type, coltype, base64 }] of Object.entries(options.data?.columnInfo ?? {})) {
            if (colname.endsWith('_chips')) {
                // do nothing
            } else if (type === 'date') {
                row[colname] = this.serializeDateValue(row[colname]);
            } else if (type === 'text' && !this.isLongTextColumn({ coltype: coltype ?? '' })) {
                row[colname] = row[colname] !== null ? `${row[colname]}`.replace(/\s/g, ' ') : '';
            } else if (this.isLongTextColumn({ coltype: coltype ?? '' })) {
                // BJS 20250612 - Added null check
                row[colname] = row[colname]?.replace(/[\u00A0\u200B-\u200F\uFEFF]/g, '') ?? '';
            }

            if (base64 === true) {
                row[colname] = this.base64.urlEncode(row[colname]);
            }
        }

        if (row['tt_gridrow_id'] !== undefined && (row['tt_gridrow_id'] === null || row['tt_gridrow_id'].length === 0)) {
            row['tt_gridrow_id'] = row._uuid;
        }
    }

    private serializeDateValue(value: unknown): string {
        if (!value) return '1970-01-01 03:00';

        const date = value instanceof Date ? value : new Date(value as string | number | Date);

        if (date.toString() === 'Invalid Date') return '1970-01-01 03:00';

        return new DatePipe('no').transform(date, 'yyyy-MM-dd HH:mm', '+0200') ?? '1970-01-01 03:00';
    }

    /**
     * Removes component specific data from the given row and return a new object without the component specific data.
     *
     * @param row the row to clean of component specific data.
     * @returns a new row without component specific data.
     */
    public getRowData<TType extends GridRow = GridRow>(options: GridOptions<TType>, row: GridRow) {
        const rowData = { ...row };

        // delete rowData._uuid; // added support for uuid to come with rowdata so it also sends it back now. id in ag-grid is ment to immutable so it cannot be set until the grid is refreshed.
        delete rowData._dirty;
        // delete rowData['is_selected'];
        delete rowData['xxxoptionsxxx'];

        if (options.data?.hasGotoParms) {
            //rowData['item_state'] = '';
            //rowData['item_parms'] = '';
            //rowData['item_path'] = '';
        }

        if (options.data?.hasThumbnails) {
            rowData['item_thumb'] = '';
        }

        return rowData;
    }

    /**
     * Based on the given operator, returns the equivelant filter type for ag-grid.
     *
     * @param operator the filter operator to find equivelant for.
     * @returns the equivelant filter operator compatiable with ag-grid.
     */
    public getFilterType(operator: 'contains' | 'doesnotcontain' | 'eq' | 'neq' | 'startswith' | 'endswith' | 'gt' | 'gte' | 'lt' | 'lte' | 'isempty' | 'isnull' | 'isnullorempty' | 'isnotempty' | 'isnotnull' | 'isnotnullorempty') {
        if (operator === 'contains') {
            return 'contains';
        } else if (operator === 'doesnotcontain') {
            return 'notContains';
        } else if (operator === 'eq') {
            return 'equals';
        } else if (operator === 'neq') {
            return 'notEqual';
        } else if (operator === 'startswith') {
            return 'startsWith';
        } else if (operator === 'endswith') {
            return 'endsWith';
        } else if (operator === 'gt') {
            return 'greaterThan';
        } else if (operator === 'gte') {
            return 'greaterThanOrEqual';
        } else if (operator === 'lt') {
            return 'lessThan';
        } else if (operator === 'lte') {
            return 'lessThanOrEqual';
        } else if (operator === 'isempty' || operator === 'isnull' || operator === 'isnullorempty') {
            return 'blank';
        } else if (operator === 'isnotempty' || operator === 'isnotnull' || operator === 'isnotnullorempty') {
            return 'notBlank';
        }

        return operator;
    }

    /**
     * Based on the given filter type, returns the equivelant filter operator type used for storing in databse.
     */
    public getFilterOperatorType(filterType: string): GridFilterType {
        if (filterType === 'contains') {
            return 'contains';
        } else if (filterType === 'notContains') {
            return 'doesnotcontain';
        } else if (filterType === 'equals') {
            return 'eq';
        } else if (filterType === 'notEqual') {
            return 'neq';
        } else if (filterType === 'startsWith') {
            return 'startswith';
        } else if (filterType === 'endsWith') {
            return 'endswith';
        } else if (filterType === 'greaterThan') {
            return 'gt';
        } else if (filterType === 'greaterThanOrEqual') {
            return 'gte';
        } else if (filterType === 'lessThan') {
            return 'lt';
        } else if (filterType === 'lessThanOrEqual') {
            return 'lte';
        } else if (filterType === 'blank') {
            return 'isnullorempty';
        } else if (filterType === 'notBlank') {
            return 'isnotnullorempty';
        }

        throw Error('filter_not_supported');
    }

    /**
     * Returns the appropriate icon classes of the given icon string. Examples of string values are `'fal-envelope'`, `'glyphicon-lock'`.
     *
     * @param icon the icon string to retrieve icon classes for.
     * @returns classes for the icon provided.
     */
    public getIconClasses(icon?: string) {
        var iconClasses = '';

        if (icon) {
            iconClasses = 'glyphicon ' + icon;

            if (icon.startsWith('fa')) {
                const preClass = icon.split(' ')[0].length < 4 ? icon.split(' ')[0] : icon.slice(0, icon.indexOf('-'));
                iconClasses = 'font-awesone-5 ' + `${preClass} fa${icon.slice(icon.indexOf('-'))}`;
            } else if (icon.startsWith('glyphicon-')) {
                iconClasses = 'glyphicon ' + icon;
            } else {
                return 'k-icon k-i-' + icon;
            }
        }

        return iconClasses;
    }

    /**
     * Sets this grid as the currently active grid.
     *
     * @param id the id of the grid to set as active grid.
     */
    public setActiveGrid(id: string) {
        this.activeGridId = id;
    }

    /**
     * Checks whether this grid is the currently active grid (the grid last interacted with).
     *
     * @param id the id of the grid to check if is the active grid.
     * @returns `true` if this grid is the active grid, `false` if not.
     */
    public isActiveGrid(id: string): boolean {
        if (this.activeGridId !== null) {
            return id === this.activeGridId;
        }

        // console.log(document.querySelectorAll('tt-grid')[0].querySelector('div')?.getAttribute('id'));
        return false;
    }

    /**
     * Returns translated text-maps to use for language in a grid.
     *
     * @param languageId the language id to use for retrieving translated text-map for grid.
     */
    public getLocaleText(languageId: string) {
        let localeText = AG_GRID_LOCALE_EN;

        switch ((languageId ?? 'en').toLowerCase()) {
            case 'cn':
                localeText = AG_GRID_LOCALE_CN;
                break;
            case 'de':
                localeText = AG_GRID_LOCALE_DE;
                break;
            case 'dk':
                localeText = AG_GRID_LOCALE_DK;
                break;
            case 'es':
                localeText = AG_GRID_LOCALE_ES;
                break;
            case 'fi':
                localeText = AG_GRID_LOCALE_FI;
                break;
            case 'fr':
                localeText = AG_GRID_LOCALE_FR;
                break;
            case 'it':
                localeText = AG_GRID_LOCALE_IT;
                break;
            case 'nn':
            case 'no':
                localeText = AG_GRID_LOCALE_NO;
                break;
            case 'pl':
                localeText = AG_GRID_LOCALE_PL;
                break;
            case 'se':
                localeText = AG_GRID_LOCALE_SE;
                break;
            case 'li':
            case 'sy':
            case 'gb':
            default:
                localeText = AG_GRID_LOCALE_EN;
                break;
        }

        return localeText;
    }

    private getErrorInfo(response: any) {
        try {
            let message: { error: boolean; code?: number; message?: string } = { error: false };

            if (!!response && response instanceof Array) {
                if (response.length !== 1 || !response[0]?.errorcode || response[0].errorcode === '0') return message;

                return { error: true, code: response[0].errorcode, message: response[0]?.errormessage || '' };
            }

            let errorInfo = message;

            if (!!response?.errorcode && response?.errorcode !== '0' && response?.errormessage !== undefined && response?.errormessage !== null) {
                errorInfo = { error: true, code: response.errorcode, message: response.errormessage };
            } else if (!!response?.data?.message) {
                errorInfo = { error: true, code: 0, message: response.data.message };
            }

            // BJS 20220619 - Handles error messages from grid update.
            if (errorInfo?.error !== true && !!response?.savedata && !!response?.savedata?.errorcode && response?.savedata?.errorcode !== 0) {
                errorInfo = { error: true, code: response.savedata.errorcode, message: response?.savedata?.errormessage };
            }

            return errorInfo;
        } catch (error) {
            console.error(error);
            return { error: false };
        }
    }

    // Used to handle buttons setup in colschema of type `datatask`.
    public async dbButtonDatatask<TType extends GridRow = GridRow>({ button, row = null, parameters, event, navigateEventEmitter, datataskEventEmitter, options, read }: DbButtonDatataskParams<TType>) {
        if (!button.p2_datatask_keyno || button.p2_datatask_keyno < 1) return;

        if (!parameters['state'] && !parameters['state_params'] && button.state && button.state_params) {
            parameters['state'] = button.state || '';
            parameters['state_params'] = button.state_params || '';
        }

        const datataskEvent: DatataskEvent = {
            preventDefault: () => (datataskEvent.defaultPrevented = true),
            defaultPrevented: false,
            p2_datatask_keyno: button.p2_datatask_keyno,
            newTab: this.utility.isModifierKeyPressed(event),
            rowData: row,
        };

        datataskEventEmitter.emit(datataskEvent);

        if (datataskEvent.defaultPrevented) return;

        try {
            options.gridfunc?.gridProgress(true);
            const response = await this.datatask.Post(button.p2_datatask_keyno, parameters);

            if (response?.[0] && button.p2_datatask_keyno === 2837) {
                const navigateEvent: NavigateEvent = {
                    preventDefault: () => (navigateEvent.defaultPrevented = true),
                    defaultPrevented: false,
                    newTab: this.utility.isModifierKeyPressed(event),
                    rowData: row,
                    itemState: response[0].item_state,
                    itemParms: response[0].item_parms,
                };

                navigateEventEmitter.emit(navigateEvent);

                if (!navigateEvent.defaultPrevented) {
                    if (navigateEvent.newTab) {
                        this.state.newTab(navigateEvent.itemState || '', navigateEvent.itemParms);
                    } else {
                        this.state.go(navigateEvent.itemState || '', navigateEvent.itemParms);
                    }
                }
            } else if (response?.messages) {
                let state: string | undefined;
                let parms: string | undefined;

                const firstGo = response.messages.find((m: { data: string }) => m.data?.startsWith('go||'));

                if (firstGo?.data) [, state, parms] = firstGo.data.split('||', 3);

                if (!!state) {
                    const navigateEvent: NavigateEvent = {
                        preventDefault: () => (navigateEvent.defaultPrevented = true),
                        defaultPrevented: false,
                        newTab: this.utility.isModifierKeyPressed(event),
                        rowData: row,
                        itemState: state,
                        itemParms: parms,
                    };

                    navigateEventEmitter.emit(navigateEvent);

                    if (!navigateEvent.defaultPrevented) {
                        if (navigateEvent.newTab) {
                            this.state.newTab(navigateEvent.itemState || '', navigateEvent.itemParms);
                        } else {
                            this.state.go(navigateEvent.itemState || '', navigateEvent.itemParms);
                        }
                    }
                } else {
                    return response;
                }
            } else {
                return response;
            }
        } catch (error) {
            throw Error(`${error}`);
        } finally {
            if (read !== false) {
                await options.gridfunc?.read();
            }

            options.gridfunc?.gridProgress(false);
        }
    }

    public getDBGridOptions(columns: GridColumn[]): { gridOptions: DBGridOptions | null; index: number } {
        return { gridOptions: columns.find((column) => column.colname === 'xxxoptionsxxx')?.gridoptions ?? null, index: columns.findIndex((column) => column.colname === 'xxxoptionsxxx') };
    }

    public async dbButtonGoto({ button, event, navigateEventEmitter, row, options }: DbButtonGotoParams) {
        // const navigateEvent: NavigateEvent = {
        //     defaultPrevented: false,
        //     preventDefault: () => (navigateEvent.defaultPrevented = true),
        let newTab = this.utility.isModifierKeyPressed(event);
        //     rowData: null,
        //     itemParms: !!button.state_params ? JSON.parse(this.atou(button.state_params || '')) : null,
        //     itemState: button.state,
        // };

        let itemState = '';
        let itemParms = '';

        if (!button.state) {
            if (!!row && !!this.datatask && !!button.name) {
                const parameters = await this.getParametersFromDataTaskId(options, 'loadData');
                const response = await this.getGotoParms({ row: row, loadDataTask: { ...options.dataTask?.loadData, parameters: parameters }, rememberId: options.dataTask?.rememberId ?? '', field: button.name });

                itemState = response.item_state;
                itemParms = response.item_parms;
            }
        }

        // navigateEventEmitter.emit(navigateEvent);

        // if (!navigateEvent.defaultPrevented) {
        if (newTab) {
            this.state.newTab(itemState || '', itemParms);
        } else {
            this.state.go(itemState || '', itemParms);
        }
        // }
    }

    public isLongTextColumn(column: { coltype: string }): boolean {
        return column.coltype === 'long varchar' || column.coltype === 'long nvarchar';
    }

    public encodeIsSelectedRows<TType extends GridRow = GridRow>(options: GridOptions<TType>) {
        const selectedRows = (options.gridfunc?.getIsSelectedRows() ?? []).map((row) => Object.assign({}, row));

        if (selectedRows.length === 0) return '';

        const textColumns: string[] = [];

        for (let column of options.data?.columnDefinitions ?? []) {
            if (!!column.colId && column.cellDataType === 'text') {
                textColumns.push(column.colId);
            }
        }

        for (let row of selectedRows) {
            for (let textColumn of textColumns) {
                (row as GridRow)[textColumn] = this.utoa(row[textColumn]);
            }
        }

        return this.utoa(JSON.stringify(selectedRows));
    }

    public async onStateClosed<TType extends GridRow = GridRow>(params: StateClosedParams<TType>) {
        if ('read_on_close' in params && !!params.read_on_close && [1, '1', true, 'true'].includes(params.read_on_close)) {
            params.grid.options.gridfunc?.read();
        } else if ('rebind_on_close' in params && !!params.rebind_on_close && [1, '1', true, 'true'].includes(params.rebind_on_close)) {
            params.grid.options.gridfunc?.rebind();
        } else if ('update_row_on_close' in params && !!params.update_row_on_close && [1, '1', true, 'true'].includes(params.update_row_on_close)) {
            if (!params.node || !params.colDef || !params.node.id) return;
            params.grid.addChangeToChanges(params.node);
            params.grid.addDirtyCellsToChanges(params.colDef, params.node);
            params.grid.saveChanges('update');
        }
    }

    public async getLoadDataParameters(params: undefined | (() => { [key: string]: any } | Promise<{ [key: string]: any }>) | any): Promise<{ [key: string]: any }> {
        let parameters: { [key: string]: any } = {};

        if (!params) return parameters;

        if (params instanceof Function) {
            const result = params();

            if (result instanceof Promise) {
                parameters = { webpage_name: this.state.getCurrentName(), ...(await result) };
            } else {
                parameters = { webpage_name: this.state.getCurrentName(), ...result };
            }
        } else {
            parameters = { webpage_name: this.state.getCurrentName(), ...params, ...parameters };
        }

        return parameters;
    }
}

export interface StateClosedParams<TType extends GridRow = GridRow> {
    read_on_close?: '1' | 1 | boolean;
    rebind_on_close?: '1' | 1 | boolean;
    update_row_on_close?: '1' | 1 | boolean;
    grid: GridComponent<TType>;
    colDef?: ColDef;
    node?: IRowNode;
}

export interface GridGotoParmsParams {
    loadDataTask?: DataTaskRequest | null;
    rememberId?: string | null;
    field: string;
    row?: GridRow | null;
    colgroup?: string | null;
    rowkey?: string | null;
}

export interface GridGotoParmsResponse {
    item_state: string;
    item_parms: any;
    item_path: string;
}

/**
 * Represents parameters to pass to `gridService.dbButtonDatatask`.
 */
export interface DbButtonDatataskParams<TType extends GridRow = GridRow> {
    /**
     * The buttons that was pressed.
     */
    button: { p2_datatask_keyno?: number; state?: string; state_params?: string };

    /**
     * The row where this button was pressed, if applicable.
     */
    row?: TType | null;

    /**
     * The parameters to pass to the datatask call.
     */
    parameters: Record<string | symbol, any>;

    /**
     * The mouse event of the click event on the button.
     */
    event: MouseEvent;

    /**
     * Event emitter for navigation event in the grid.
     */
    navigateEventEmitter: EventEmitter<NavigateEvent>;

    /**
     * Event emitter for datatask event in the grid.
     */
    datataskEventEmitter: EventEmitter<DatataskEvent>;

    /**
     * The grid options of the grid.
     */
    options: GridOptions<TType>;

    /**
     * Whether to call read after the datatask is finished.
     */
    read?: boolean;
}

export interface DbButtonGotoParams {
    /**
     * The buttons that was pressed.
     */
    button: { name: string; state?: string; state_params?: unknown };

    /**
     * The mouse event of the click event on the button.
     */
    event: MouseEvent;

    /**
     * Event emitter for navigation event in the grid.
     */
    navigateEventEmitter: EventEmitter<NavigateEvent>;

    row?: GridRow;

    colId?: string;

    options: GridOptions;
}

export interface SaveRowParams<TType extends GridRow = GridRow> {
    options: GridOptions<TType>;
    rowIndex: number | null;
    node: IRowNode | null;
    event: GridRowChangeEvent;
    state: ChangeState;
    method: string | number;
    saveParameters: any;
    data: TType;
    rowIdOver: string | null;
    direction: string | null;
}

export interface SaveRowResponse<TType extends GridRow = GridRow> extends _SaveRowResponse {
    node: IRowNode | null;
    data: TType;
}

interface _SaveRowParams {
    method: string | number;
    parameters: {
        dirtyCells: string;
        load_datatask_keyno: number;
        event: GridRowChangeEvent;
        rowIndex: number | null;
        state: ChangeState;
        changes: GridRow;
        rowIdOver: string | null;
        direction: string | null;
    };
}

export interface _SaveRowResponse {
    errorcode: number;
    errormessage: string;
    read: 1 | 0;
    savedata: GridRow;
    messages: GridMessage[];
}

export interface GridMessage {
    /**
     * @default 'toast'
     */
    display_type: string;

    /**
     * @default 7000
     */
    duration?: number;

    id: string;

    keyno: number;

    message: string;

    title: string;

    /**
     * @default 'warning'
     */
    type?: 'primary' | 'success' | 'warning' | 'danger';

    item_state?: string;

    item_parms?: string;

    gototext?: string;
}

export type GridMessageQueueItem = GridMessage | GridMessage[];

export type GridMessageType = NonNullable<GridMessage['type']>;

interface PopupEvent {
    type: 'ttData';
    data?: { [key: string]: string };
    messages?: GridMessage[] | string;
}
